import React, { useCallback, useMemo, useState } from 'react';
import cn from '../../utils/cn';
import { IconNoPhotographyOutline } from '../../icons';
import invariant from 'ts-invariant';

const Image = (
  props: React.ComponentPropsWithoutRef<'img'> & React.PropsWithChildren,
) => {
  return (
    <Root {...props}>
      <Img />
      <Loading />
      <Error />
    </Root>
  );
};

const ImageContext = React.createContext<
  | ({
      isError: boolean;
      isLoading: boolean;
    } & React.ComponentPropsWithoutRef<'img'>)
  | undefined
>(undefined);

const useImage = () => {
  const context = React.useContext(ImageContext);
  invariant(context, 'useImage must be used within a ImageContext component');
  return context;
};

const Root = ({
  children,
  className,
  ...props
}: React.ComponentPropsWithoutRef<'img'> & React.PropsWithChildren) => {
  const [isError, setErrors] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const handleError = useCallback(() => {
    setErrors(true);
    setIsLoading(false);
  }, [setErrors, setIsLoading]);

  const handleLoading = useCallback(() => {
    setIsLoading(false);
  }, [setIsLoading]);

  const value = useMemo(
    () => ({
      isError,
      onError: handleError,
      onLoad: handleLoading,
      isLoading,
      ...props,
    }),
    [handleError, handleLoading, isError, isLoading, props],
  );

  return (
    <ImageContext.Provider value={value}>
      <div className={cn('relative h-full w-full overflow-hidden', className)} {...props}>
        {children}
      </div>
    </ImageContext.Provider>
  );
};

const Img = ({
  className,
  alt,
  loading,
  ...props
}: React.ComponentPropsWithoutRef<'img'>) => {
  const {
    isLoading,
    isError,
    alt: contextAlt,
    loading: contextLoading,
    ...context
  } = useImage();

  return (
    <img
      loading={loading ?? contextLoading ?? 'lazy'}
      alt={alt ?? contextAlt ?? ''}
      {...context}
      {...props}
      className={cn(
        'h-full w-full object-cover',
        {
          hidden: isError,
        },
        className,
      )}
      aria-hidden={isLoading || isError}
    />
  );
};

const Loading = ({
  children,
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) => {
  const { isLoading } = useImage();

  if (!isLoading) {
    return null;
  }

  return (
    <div
      role="status"
      aria-label="Chargement en cours"
      className={cn(
        'absolute inset-0 flex items-center justify-center overflow-hidden bg-white',
        className,
      )}
      {...props}
    >
      {children ?? <div className="h-full w-full animate-pulse bg-neutral-300" />}
    </div>
  );
};

const Error = ({
  children,
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) => {
  const { isLoading, isError } = useImage();

  if (!isError || isLoading) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-label="Erreur de chargement"
      className={cn(
        'absolute inset-0 flex items-center justify-center bg-white',
        className,
      )}
      {...props}
    >
      {children ?? <IconNoPhotographyOutline className="size-2/5 fill-neutral-300" />}
    </div>
  );
};

Image.Root = Root;
Image.Img = Img;
Image.Loading = Loading;
Image.Error = Error;

export default Image;
